import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MENU = [
  { to: "/", label: "On This Day", testid: "menu-on-this-day", match: (p) => p === "/", bar: true },
  { to: "/browse", label: "Search", testid: "menu-search", match: (p) => p === "/browse" },
  { to: "/number-ones", label: "No.1 Charts", testid: "menu-number-ones", match: (p) => p.startsWith("/number-ones"), bar: true },
  { to: "/quiz", label: "History Quiz - Music & Sports", testid: "menu-quiz", match: (p) => p.startsWith("/quiz"), bar: "Quiz" },
  { to: "/died", label: "Died on this day", testid: "menu-died", match: (p) => p.startsWith("/died"), bar: "Died" },
  { to: "/sports", label: "Sports Almanac", testid: "menu-sports", match: (p) => p.startsWith("/sports"), bar: "Sports" },
  { to: "/battles", label: "Historic Battles", testid: "menu-battles", match: (p) => p.startsWith("/battles"), bar: "Battles" },
  { to: "/movies", label: "The Movies", testid: "menu-movies", match: (p) => p.startsWith("/movies"), bar: "Movies" },
  { to: "/birthdays", label: "Birthdays", testid: "menu-birthdays", match: (p) => p.startsWith("/birthdays"), bar: true },
  { to: "/horoscope", label: "Horoscope", testid: "menu-horoscope", match: (p) => p === "/horoscope" },
  { to: "/saved", label: "Saved", testid: "menu-saved", match: (p) => p === "/saved" },
  { to: "/about", label: "About", testid: "menu-about", match: (p) => p === "/about" },
  { to: "/contact", label: "Contact", testid: "menu-contact", match: (p) => p === "/contact" },
  { to: "/privacy", label: "Privacy", testid: "menu-privacy", match: (p) => p === "/privacy" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => { if (searchOpen) inputRef.current?.focus(); }, [searchOpen]);

  const submitSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setSearchOpen(false);
    setOpen(false);
    setQ("");
    navigate(`/browse?search=${encodeURIComponent(term)}`);
  };

  const doLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const menuClass = "flex items-center gap-2 py-2.5 text-[15px] font-medium text-ink hover:text-gold transition-colors";
  const iconBtn = "w-10 h-10 rounded-full border border-[#e5e7eb] bg-white text-ink hover:border-gold flex items-center justify-center transition-colors";

  return (
    <header className="sticky top-0 z-50 bg-paper border-b border-[#e5e7eb]">
      <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4 gap-3">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5 shrink-0">
          <img src="/seal-oddlytrue-red-wax.png" alt="OddlyTrue" className="w-11 h-11 object-contain seal-stamp" />
          <span className="text-[22px] font-bold tracking-tight text-ink">
            Oddly<span className="text-gold">True</span>
          </span>
        </Link>
        <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
          {searchOpen ? (
            <form onSubmit={submitSearch} data-testid="header-search-form" className="flex items-center gap-2 flex-1 max-w-md">
              <input
                ref={inputRef}
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") setSearchOpen(false); }}
                placeholder="Search history, birthdays, years..."
                className="w-full h-10 rounded-full border border-gold bg-white px-4 text-[14px] text-ink placeholder:text-ink-muted outline-none"
              />
              <button type="submit" data-testid="header-search-submit" aria-label="Search" className={`${iconBtn} shrink-0`}><Search className="w-4 h-4" /></button>
              <button type="button" data-testid="header-search-close" aria-label="Close search" onClick={() => setSearchOpen(false)} className={`${iconBtn} shrink-0`}><X className="w-4 h-4" /></button>
            </form>
          ) : (
            <button type="button" data-testid="header-search-toggle" aria-label="Open search" onClick={() => { setSearchOpen(true); setOpen(false); }} className={iconBtn}>
              <Search className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="menu-toggle"
            onClick={() => { setOpen(!open); setSearchOpen(false); }}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className={`${iconBtn} shrink-0`}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <nav data-testid="nav-bar" aria-label="Key sections" className="hidden md:block border-t border-[#e5e7eb]/80 bg-paper">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          {MENU.filter((n) => n.bar).map((n) => {
            const label = typeof n.bar === "string" ? n.bar : n.label;
            const active = n.match(pathname);
            const cls = `relative shrink-0 px-3 h-10 inline-flex items-center text-[13px] font-semibold tracking-wide transition-colors ${active ? "text-ink" : "text-ink-muted hover:text-ink"} after:absolute after:left-3 after:right-3 after:bottom-0 after:h-[2px] after:bg-gold after:transition-transform after:origin-left ${active ? "after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"}`;
            return <Link key={n.to} to={n.to} data-testid={`nav-${n.testid}`} aria-current={active ? "page" : undefined} className={cls}>{label}</Link>;
          })}
        </div>
      </nav>

      {open && (
        <nav data-testid="site-menu" className="border-t border-[#e5e7eb] bg-paper">
          <div className="max-w-7xl mx-auto px-6 py-4 grid grid-cols-1 sm:grid-cols-3 gap-1">
            {MENU.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={n.testid}
                onClick={() => setOpen(false)}
                className={menuClass}
              >
                {n.match(pathname) && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
                {n.label}
              </Link>
            ))}
            {!user && (
              <Link to="/login" data-testid="menu-signin" onClick={() => setOpen(false)} className={`${menuClass} text-ink-muted`}>Sign in</Link>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" data-testid="menu-admin" onClick={() => setOpen(false)} className={`${menuClass} text-gold`}>
                {pathname.startsWith("/admin") && <span className="w-1.5 h-1.5 rounded-full bg-gold" />}
                Admin
              </Link>
            )}
            {user && (
              <button data-testid="menu-signout" onClick={doLogout} className="flex items-center py-2.5 text-[15px] font-medium text-ink-muted hover:text-gold text-left">
                Sign out{user.role === "admin" ? " (admin)" : ""}
              </button>
            )}
          </div>
        </nav>
      )}
    </header>
  );
};
